import { useEffect, useRef } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit-react';
import { useAccounts, useCurrentWallet } from './walletCompat';
import { useBalances } from '../context/BalanceContext';
import { usePosition } from '../context/PositionContext';
import { useSubscription } from '../hooks/useSubscription';
import { Tracker } from '../utils/tracker';
export function useWalletTracker() {
    const currentAccount = useCurrentAccount();
    const accounts = useAccounts();
    const { currentWallet } = useCurrentWallet();
    const { walletBalancesMap, isLoading: balancesLoading } = useBalances();
    const { allPositionCapIds, loading: positionsLoading } = usePosition();
    const { data: coinInfoData } = useSubscription('coin_info');
    const trackedAddressRef = useRef(null);
    const previousAddressRef = useRef(null);
    const hasPendingTrackRef = useRef(false);
    // Capture previous address immediately when account changes,
    // before async data (balances, positions) is ready.
    // trackedAddressRef is updated here (not in Effect 2) so a rapid second switch
    // before data loads still sees the correct previous address.
    // Mixpanel: reset + identify when switching accounts (not first connect) so the new wallet
    // does not inherit the prior distinct_id session (fixes errAnonDistinctIdAssignedAlready).
    useEffect(() => {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address))
            return;
        if (currentAccount.address === trackedAddressRef.current)
            return;
        const hadPriorWallet = trackedAddressRef.current !== null;
        previousAddressRef.current = trackedAddressRef.current;
        trackedAddressRef.current = currentAccount.address;
        hasPendingTrackRef.current = true;
        Tracker.identify(currentAccount.address, { newSession: hadPriorWallet });
    }, [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address]);
    // Fire the event once all async data has settled.
    useEffect(() => {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) ||
            !hasPendingTrackRef.current ||
            positionsLoading ||
            balancesLoading ||
            walletBalancesMap === undefined ||
            coinInfoData == null) {
            return;
        }
        hasPendingTrackRef.current = false;
        const previousAddress = previousAddressRef.current;
        const walletName = currentWallet && typeof currentWallet.name === 'string'
            ? currentWallet.name.toLowerCase()
            : null;
        let totalUsd = 0;
        const tokenAmounts = {};
        const tokenUsdValues = {};
        for (const [coinType, rawBalance] of walletBalancesMap.entries()) {
            const info = coinInfoData[coinType];
            if (!info)
                continue;
            // rawBalance is a raw integer string (e.g. "1000000000" = 1 SUI with 9 decimals)
            const amount = parseFloat(rawBalance) / Math.pow(10, parseInt(info.decimals));
            const usd = amount * parseFloat(info.price);
            if (amount > 0) {
                tokenAmounts[info.symbol] = parseFloat(amount.toFixed(6));
                tokenUsdValues[info.symbol] = parseFloat(usd.toFixed(2));
                totalUsd += usd;
            }
        }
        const distinctAddresses = [...new Set(accounts.map((a) => a.address))];
        const totalWalletCount = distinctAddresses.length;
        const otherAddresses = distinctAddresses.filter((addr) => addr !== currentAccount.address);
        const commonProps = {
            $PositionCount: allPositionCapIds.length,
            $TotalWalletCount: totalWalletCount,
            $OtherWalletAddress: otherAddresses,
            $Wallet: walletName,
            $WalletBalance: `$${parseFloat(totalUsd.toFixed(2))}`, // total USD across all tokens
            $TokenAmounts: tokenAmounts, // per-token quantities
            $TokenUsdValues: Object.fromEntries(
            // per-token USD values with $ prefix
            Object.entries(tokenUsdValues).map(([sym, val]) => [sym, `$${val}`])),
        };
        if (previousAddress !== null) {
            Tracker.track('Wallet Switched', Object.assign(Object.assign({}, commonProps), { $PreviousWallet: previousAddress }));
        }
        else {
            Tracker.track('Wallet Connected', commonProps);
        }
    }, [
        currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address,
        currentWallet,
        positionsLoading,
        balancesLoading,
        walletBalancesMap,
        coinInfoData,
        allPositionCapIds,
        accounts,
    ]);
    // Reset on disconnect so the next fresh connect fires "Wallet Connected".
    useEffect(() => {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address)) {
            trackedAddressRef.current = null;
            previousAddressRef.current = null;
            hasPendingTrackRef.current = false;
            Tracker.reset();
        }
    }, [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address]);
}
